import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getObstacleIcon, getShieldComboIcon } from '../../utils/markers';
import L from 'leaflet';
import * as turf from '@turf/turf';
import 'leaflet/dist/leaflet.css';
import { addHullPolygonsToMap, getObstaclesToRender } from '../../utils/hullMapLayer';
import Cesium3DPreviewModal from '../../components/Cesium3DPreviewModal';
import { T } from '../../assets/theme';
import { useSurfaceStyles } from '../../assets/SurfaceStyleContext';
import { DraggableModal, ResizeHandle } from '../../components/UI';
import { useResizableHeight } from '../../utils/useResizableHeight';
import MapMeasureTool from '../../components/MapMeasureTool';
import MapCoordinateViewer from '../../components/MapCoordinateViewer';
import { useShield, exportSurfacePenetratingCsv } from '../../utils/useShield';
import ShieldControl from '../../components/ShieldControl';
import { getCtrMaskingAngle, getCtrByIcao } from '../../api/client';
import { formatAirspaceLimit } from '../../utils/airspaceStyle';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const exportToCsv = (headers, rows, filename) => {
    const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(val => {
            const cleanVal = String(val ?? '').replace(/—/g, '-');
            return `"${cleanVal.replace(/"/g, '""')}"`;
        }).join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// TILE_LAYERS constants for map backgrounds
const TILE_LAYERS = {
    dark: { label: '🌙 Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CARTO' },
    street: { label: '🗺 Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OSM' },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri' },
};

const DEG = Math.PI / 180;
function haversine(lat1, lon1, lat2, lon2) {
    const R = 6371000;
    const dLat = (lat2 - lat1) * DEG, dLon = (lon2 - lon1) * DEG;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * DEG) * Math.cos(lat2 * DEG) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
const BRA_TYPES = {
    DME: { rCyl: 300, aCone: 1, rCone: 3000, jCyl: 0, hHeight: 0 },
    CVOR: { rCyl: 600, aCone: 1, rCone: 3000, jCyl: 15000, hHeight: 52 },
    VOR: { rCyl: 600, aCone: 1, rCone: 3000, jCyl: 15000, hHeight: 52 },
    DVOR: { rCyl: 600, aCone: 1, rCone: 3000, jCyl: 10000, hHeight: 52 },
    DF: { rCyl: 500, aCone: 1, rCone: 3000, jCyl: 10000, hHeight: 52 },
    DIRECTION: { rCyl: 500, aCone: 1, rCone: 3000, jCyl: 10000, hHeight: 52 },
    MARKER: { rCyl: 50, aCone: 20, rCone: 200, jCyl: 0, hHeight: 0 },
    NDB: { rCyl: 200, aCone: 5, rCone: 1000, jCyl: 0, hHeight: 0 },
    'GBAS REF': { rCyl: 400, aCone: 3, rCone: 3000, jCyl: 0, hHeight: 0 },
    'GBAS VDB': { rCyl: 300, aCone: 0.9, rCone: 3000, jCyl: 0, hHeight: 0 },
    VDB: { rCyl: 300, aCone: 0.9, rCone: 3000, jCyl: 0, hHeight: 0 },
    PSR: { rCyl: 500, aCone: 0.25, rCone: 15000, jCyl: 0, hHeight: 0 },
    SSR: { rCyl: 500, aCone: 0.25, rCone: 15000, jCyl: 0, hHeight: 0 },
    VHF: { rCyl: 300, aCone: 1, rCone: 2000, jCyl: 0, hHeight: 0 },
};

const FEATTYPE_TO_FACILITY_KEY = {
    "ILS LOC": "ILS_LLZ_Single",
    "ILS LOC 1": "ILS_LLZ_Single",
    "ILS GP": "ILS_GP",
    "ILS DME": "DME_Dir",   // same params as standalone DME
    "DME": "DME_Dir",
    "VOR": "DME_Dir",
    "VORTAC": "DME_Dir",
};

const NAV_DIR = {
    "ILS_LLZ_Single": { b: 500, D: 500, H: 10, h: 70, L: 2300, phi: 30, r_extra: 6000 },
    "ILS_LLZ_Dual": { b: 500, D: 500, H: 20, h: 70, L: 1500, phi: 20, r_extra: 6000 },
    "ILS_GP": { b: 50, D: 250, H: 5, h: 70, L: 325, phi: 10, r_fixed: 6000, A_fixed: 800 },
    "MLS_AZ": { b: 20, D: 600, H: 20, h: 70, L: 1500, phi: 40, r_extra: 6000 },
    "MLS_EL": { b: 20, D: 200, H: 20, h: 70, L: 1500, phi: 40, r_fixed: 6000, A_fixed: 300 },
    // DME directional (ILS DME, standalone DME, VORTAC): b=20, D=600, H=20, h=70, L=1500, phi=40
    "DME_Dir": { b: 20, D: 600, H: 20, h: 70, L: 1500, phi: 40, r_extra: 6000 },
};

function computeDestination(lat, lon, distanceM, bearingDeg) {
    const R = 6371000;
    const brng = bearingDeg * DEG;
    const lat1 = lat * DEG;
    const lon1 = lon * DEG;
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(distanceM / R) + Math.cos(lat1) * Math.sin(distanceM / R) * Math.cos(brng));
    const lon2 = lon1 + Math.atan2(Math.sin(brng) * Math.sin(distanceM / R) * Math.cos(lat1), Math.cos(distanceM / R) - Math.sin(lat1) * Math.sin(lat2));
    return [lat2 / DEG, lon2 / DEG];
}

// localToGlobal: converts local (X=along-heading, Y=right-of-heading) offsets in meters
// to a WGS84 [lat, lon] coordinate given the antenna position and runway heading.
function localToGlobal(lx, ly, antLat, antLon, hdgDeg) {
    const dist = Math.hypot(lx, ly);
    if (dist < 0.001) return [antLat, antLon];
    // atan2(y, x) gives the angle from the +X axis (i.e. from runway heading)
    // but we use atan2(ly, lx) to get the angle offset from hdgDeg
    const angOffset = Math.atan2(ly, lx) / DEG;
    return computeDestination(antLat, antLon, dist, hdgDeg + angOffset);
}

function getParams(type) {
    const t = (type || '').toUpperCase();
    // Check full-key matches first (prevents 'ILS DME' matching bare 'DME')
    for (const [k, v] of Object.entries(BRA_TYPES)) { if (t === k) return v; }
    // Then substring fallback for types like 'ILS LOC 1'
    for (const [k, v] of Object.entries(BRA_TYPES)) { if (t.includes(k) && !('ILS ' + k === t.slice(0, 4 + k.length))) return v; }
    return BRA_TYPES.NDB;
}

function computeBRA(cns, obstacle) {
    const cLat = cns.lat_cns || cns.lat, cLon = cns.lon_cns || cns.lon;
    const oLat = obstacle.latitude, oLon = obstacle.longitude;
    if (!cLat || !cLon || !oLat || !oLon) return null;
    const p = getParams(cns.type || cns.feattype);
    const distM = haversine(cLat, cLon, oLat, oLon);
    const htol = parseFloat(obstacle.h_tolerance || 0);
    const eff = Math.max(0, distM - htol);
    // True bearing from the facility to the obstacle — geometric, always
    // computable regardless of whether this facility has its own heading
    // (that's what decides directional vs. omni treatment, a separate thing).
    const brng = Math.round(computeBearing(cLat, cLon, oLat, oLon) * 10) / 10;

    const obstElevM = parseFloat(obstacle.elevation_m || 0);
    // elev_m is gnd_elev_m from the DB (already in metres)
    const cnsElevM = parseFloat(cns.elev_m ?? cns.navaidelev ?? 0);

    const obstType = (obstacle.obstacle_type || '').toUpperCase();
    const isWef = obstType.includes('WEF') || obstType.includes('WIND') || obstType.includes('TURBINE');

    let surface = 'Outside Protected Area', braElev = null, penetration = null, outcome = 'outside surfaces';

    if (eff <= p.rCyl) {
        braElev = cnsElevM;
        penetration = obstElevM - braElev;
        return { distM: Math.round(eff), brng, surface: 'r-cylinder', braElev: braElev.toFixed(2), penetration: penetration.toFixed(2), outcome: 'CNS Engineer' };
    }

    let candidates = [];
    if (eff <= p.rCone) {
        let elev = cnsElevM + eff * Math.tan(p.aCone * DEG);
        candidates.push({ surface: 'R-cone', elev });
    }
    if (isWef && p.jCyl > 0 && eff <= p.jCyl) {
        let elev = cnsElevM + p.hHeight;
        candidates.push({ surface: 'j-cylinder', elev });
    }

    if (candidates.length === 0) {
        return { distM: Math.round(eff), brng, surface: '—', braElev: '—', penetration: '—', outcome: 'outside surfaces' };
    }

    candidates.sort((a, b) => a.elev - b.elev);
    const worst = candidates[0];
    penetration = obstElevM - worst.elev;
    outcome = penetration > 0 ? 'penetrated' : 'not penetrated';

    return {
        distM: Math.round(eff),
        brng,
        surface: worst.surface,
        braElev: worst.elev.toFixed(2),
        penetration: penetration.toFixed(2),
        outcome
    };
}

function computeBearing(lat1, lon1, lat2, lon2) {
    const l1 = lat1 * DEG, l2 = lat2 * DEG;
    const dl = (lon2 - lon1) * DEG;
    const y = Math.sin(dl) * Math.cos(l2);
    const x = Math.cos(l1) * Math.sin(l2) - Math.sin(l1) * Math.cos(l2) * Math.cos(dl);
    return (Math.atan2(y, x) / DEG + 360) % 360;
}

// Vertical (elevation) angle from the CNS facility's phase centre to the top
// of an obstacle — atan(Δheight / horizontal distance), in degrees, positive
// = the obstacle top sits above the facility. This is the line-of-sight
// angle used to judge whether a candidate shielding structure is actually
// steep/close enough to sit in front of — and block signal to — the point
// it's being compared against, independent of the flat BRA-surface check.
function computeVerticalAngleDeg(cns, obstacle) {
    const cLat = cns.lat_cns || cns.lat, cLon = cns.lon_cns || cns.lon;
    const oLat = obstacle.latitude, oLon = obstacle.longitude;
    if (!cLat || !cLon || !oLat || !oLon) return null;
    const distM = haversine(cLat, cLon, oLat, oLon);
    if (distM < 0.5) return null;
    const cnsElevM = parseFloat(cns.elev_m ?? cns.navaidelev ?? 0);
    const obsTopElevM = parseFloat(obstacle.elevation_m || 0);
    return Math.atan2(obsTopElevM - cnsElevM, distM) / DEG;
}

// A "sloping" BRA surface is one whose height limit grows with distance from
// the facility (R-cone for omni facilities, Zone 3 for directional ones) —
// as opposed to a flat-height zone (r-cylinder, j-cylinder, ground plane,
// side area) where proximity to the facility carries no shielding meaning.
function isSlopingBraSurface(surface) {
    return surface === 'R-cone' || surface === 'Dir - Sloping';
}

// Local planar (east/north, metres) offset of a lat/lon point relative to a
// reference point — accurate enough at BRA-zone scale (a few km at most).
function toLocalMeters(refLat, refLon, lat, lon) {
    const R = 6371000;
    const north = (lat - refLat) * DEG * R;
    const east = (lon - refLon) * DEG * R * Math.cos(refLat * DEG);
    return [east, north];
}

// A shielding structure can only stand in the true line of sight to a given
// project obstacle if it falls close to the straight line from the CNS
// facility to that obstacle, AND between the two (not off to the side, and
// not beyond the obstacle itself). Off that line, it's an independent BRA
// violation of its own, not evidence the project obstacle is shielded.
// The allowed lateral offset is the candidate's own surveyed horizontal
// tolerance (h_tolerance_m) — a candidate is "on the line" if the line
// could realistically pass through it given its known position accuracy.
// Falls back to this default only when a candidate has no tolerance on record.
const ON_LINE_LATERAL_TOLERANCE_M = 50;
function computeLineAlignment(cns, projectObstacle, candidate, toleranceM = ON_LINE_LATERAL_TOLERANCE_M) {
    const cLat = cns.lat_cns || cns.lat, cLon = cns.lon_cns || cns.lon;
    if (!cLat || !cLon || !projectObstacle?.latitude || !projectObstacle?.longitude ||
        !candidate?.latitude || !candidate?.longitude) return null;
    const [ax, ay] = toLocalMeters(cLat, cLon, projectObstacle.latitude, projectObstacle.longitude);
    const [bx, by] = toLocalMeters(cLat, cLon, candidate.latitude, candidate.longitude);
    const segLenSq = ax * ax + ay * ay;
    if (segLenSq < 1) return null; // project obstacle essentially at the facility — no meaningful line
    const t = (bx * ax + by * ay) / segLenSq; // 0 at facility, 1 at the project obstacle
    const lateralOffsetM = Math.abs(bx * ay - by * ax) / Math.sqrt(segLenSq);
    const betweenNavaidAndObstacle = t > 0 && t < 1;
    return {
        lateralOffsetM,
        betweenNavaidAndObstacle,
        toleranceM,
        onLine: betweenNavaidAndObstacle && lateralOffsetM <= toleranceM,
    };
}

function computeDirectionalBra(cns, obstacle) {
    const cLat = cns.lat_cns || cns.lat;
    const cLon = cns.lon_cns || cns.lon;
    const oLat = obstacle.latitude;
    const oLon = obstacle.longitude;
    const fkey = FEATTYPE_TO_FACILITY_KEY[(cns.feattype || '').toUpperCase()];
    if (!cLat || !cLon || !oLat || !oLon || !fkey || !NAV_DIR[fkey]) return null;

    const p = NAV_DIR[fkey];
    const b = p.b, D = p.D, H = p.H, h = p.h, sideL = p.L;

    const ft = (cns.feattype || '').toUpperCase();
    const A = p.A_fixed !== undefined ? p.A_fixed
        : (cns.A_m != null ? cns.A_m : (ft.startsWith('ILS LOC') ? 3000 : ft.startsWith('ILS DME') ? 900 : 0));
    const r = p.r_fixed !== undefined ? p.r_fixed : (A + (p.r_extra || 0));

    const H_over_h_div_r = H * (r / h);
    const ix_x = A + H_over_h_div_r;

    const navElev = parseFloat(cns.elev_m ?? 0);
    const obsElev = parseFloat(obstacle.elevation_m || 0);
    const vtol = parseFloat(obstacle.v_tolerance || 0);
    const totalElev = obsElev + vtol;

    const distM = Math.round(haversine(cLat, cLon, oLat, oLon));

    const isIlsType = ft.startsWith('ILS');
    const hdgDeg = isIlsType ? (cns.heading_deg + 180) % 360 : (cns.heading_deg || 0);

    const brng = computeBearing(cLat, cLon, oLat, oLon);
    const relAngDeg = ((brng - hdgDeg) + 360) % 360;
    const relAngRad = relAngDeg * DEG;

    const lx = distM * Math.cos(relAngRad);
    const ly = Math.abs(distM * Math.sin(relAngRad)); // Use symmetry

    // Function for Divergence Line from (A, D) to (ix_x, sideL)
    const y_ray = (x) => D + (x - A) * ((sideL - D) / H_over_h_div_r);

    const brngRounded = Math.round(brng * 10) / 10;

    // Zone 1 — Ground Plane
    if (lx >= -b && lx <= A && ly <= D) {
        return { distM, brng: brngRounded, surface: 'Dir - Ground Plane', braElev: '0.00', penetration: 'N/A', outcome: 'CNS Engineer' };
    }

    // Zone 2 — Side Areas (Rectangles up to ix_x)
    if (lx >= -b && lx <= ix_x && ly <= sideL && ly > (lx >= A ? y_ray(lx) : D)) {
        const surf_e = navElev + H;
        const pen = totalElev - surf_e;
        return { distM, brng: brngRounded, surface: 'Dir - Side Area', braElev: surf_e.toFixed(2), penetration: pen.toFixed(2), outcome: pen > 0 ? 'penetrated' : 'not penetrated' };
    }

    // Zone 3 — Sloping Surface (Central wedge up to r arc)
    if (lx >= A && distM <= r && ly <= y_ray(lx)) {
        const slope_k = h / (r - A);
        const surf_e = navElev + (lx - A) * slope_k;
        const pen = totalElev - surf_e;
        return { distM, brng: brngRounded, surface: 'Dir - Sloping', braElev: surf_e.toFixed(2), penetration: pen.toFixed(2), outcome: pen > 0 ? 'penetrated' : 'not penetrated' };
    }

    return { distM, brng: brngRounded, surface: '—', braElev: '—', penetration: '—', outcome: 'outside surfaces' };
}

// SVG icon helpers
function svgIcon(url, size = 30) {
    const h = Math.round(size / 2);
    return L.divIcon({
        className: '',
        html: `<img src="${url}" width="${size}" height="${size}" style="display:block;">`,
        iconSize: [size, size], iconAnchor: [h, h], popupAnchor: [0, -h],
    });
}
function cnsIconUrl(type) {
    const t = (type || '').toUpperCase();
    if (t.includes('VORTAC')) return '/symbols/VORTAC.svg';
    if (t.includes('VOR') || t.includes('CVOR') || t.includes('DVOR')) return '/symbols/VOR.svg';
    if (t.includes('ILS LOC') || t.startsWith('LOC')) return '/symbols/LOC.svg';
    if (t.includes('ILS GP') || t.includes('ILS DME') || t.includes('GP')) return '/symbols/GPDME.svg';
    if (t.includes('DME')) return '/symbols/DMEVOR.svg';
    return null;
}
function obsIcon(outcome, o, showObsLabels) {
    return getObstacleIcon(L, o, outcome, showObsLabels);
}

function StatusBadge({ val }) {
    const v = val || '';
    const map = {
        'penetrated': { c: T.danger, label: '⚡ PENETRATED' },
        'CNS Engineer': { c: '#f5a623', label: '⚠ CNS ENGINEER' },
        'not penetrated': { c: T.safe, label: '✔ NOT PENETRATED' },
        'outside surfaces': { c: T.muted, label: '— OUTSIDE SURFACES' }
    };
    const { c, label } = map[v] || { c: T.muted, label: v };
    return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, color: '#fff', background: c + '33', border: `1px solid ${c}55` }}>{label}</span>;
}

export default function CNSBraPage({ project, arps, cns, obstacles, loading, state, setState, showObstacles = true, showObsLabels, showArpLabels, onObstacleContextMenu, onMapReady }) {
    const mapRef = useRef(null), mapInst = useRef(null), tileRef = useRef(null), layersRef = useRef([]);
    // Step 2 reference set — this project's own obstacles that actually
    // penetrate a CNS/BRA zone, not just its tallest point overall. Mirrors
    // the validCns filter below (kept separate here to avoid reordering the
    // existing hooks) and reuses the same computeBRA/computeDirectionalBra
    // pure functions the marker-render effect uses per-obstacle.
    const dirKeysForShield = ["ILS LOC", "ILS GP", "ILS DME", "VORTAC"];
    const omniKeysForShield = ["CVOR", "VOR", "DVOR", "DF", "DIRECTION", "MARKER", "NDB", "GBAS", "VDB", "PSR", "SSR", "VHF", "DME"];
    const penetratingObstacleIds = React.useMemo(() => {
        const validCnsForShield = (cns || []).filter(c => {
            const typ = (c.feattype || c.type || "").toUpperCase();
            return dirKeysForShield.some(k => typ.startsWith(k)) || omniKeysForShield.some(k => typ === k || (typ.startsWith(k) && !typ.startsWith('ILS')));
        });
        const ids = [];
        (obstacles || []).forEach(o => {
            const pen = validCnsForShield.some(c => {
                const fkey = FEATTYPE_TO_FACILITY_KEY[(c.feattype || '').toUpperCase()];
                const isDirectional = fkey && NAV_DIR[fkey] && c.heading_deg != null && c.A_m != null;
                const res = isDirectional ? computeDirectionalBra(c, o) : computeBRA(c, o);
                return res && (res.outcome === 'penetrated' || res.outcome === 'CNS Engineer');
            });
            if (pen) ids.push(o.id);
        });
        return ids;
    }, [cns, obstacles]);
    const { shieldOn, toggleShield, shieldData, shieldLoading, shieldError, projectMaxTotalElev, projectPenetratingMaxTotalElev, radiusKm, setRadiusKm, exportCsv, shieldLayerRef } = useShield(mapInst, obstacles, penetratingObstacleIds);
    const { getStyle } = useSurfaceStyles();
    const groundPlaneStyle = getStyle('cns_bra.ground_plane');
    const sideAreaStyle = getStyle('cns_bra.side_area');
    const slopingSurfaceStyle = getStyle('cns_bra.sloping_surface');
    const rCylinderStyle = getStyle('cns_bra.r_cylinder');
    const rConeStyle = getStyle('cns_bra.r_cone');
    const jCylinderStyle = getStyle('cns_bra.j_cylinder');
    
    // Helpers to update complex state prop
    const setVisible = useCallback((v) => setState(s => {
        const next = typeof v === 'function' ? v(s.visible) : v;
        return { ...s, visible: next };
    }), [setState]);
    const setSelObstId = useCallback((id) => setState(s => ({ ...s, selObstId: id })), [setState]);
    const setMapMode = useCallback((m) => setState(s => ({ ...s, mapMode: m })), [setState]);

    const { visible, selObstId, mapMode } = state;
    const [mapState, setMapState] = useState(null);
    const [capturing, setCapturing] = useState(false);
    const [resultsCollapsed, setResultsCollapsed] = useState(false);
    const [resultsHeight, startResultsResize] = useResizableHeight(220, { min: 120, max: 700 });
    const [resultsPoppedOut, setResultsPoppedOut] = useState(false);
    const [requireOnLine, setRequireOnLine] = useState(true);
    const [showShieldLines, setShowShieldLines] = useState(false);
    const [showHighestElevLines, setShowHighestElevLines] = useState(false);
    const [showCtr, setShowCtr] = useState(false);
    const [ctrData, setCtrData] = useState(null);
    const [ctrError, setCtrError] = useState('');
    // Operational altitude for the CTR obstruction/masking-angle checks —
    // e.g. FAOR's 7000 ft circuit altitude. There's no reliable per-aerodrome
    // default (a CTR's own published floor is SFC/0 ft, which no aircraft
    // flies at), so this is entered here and shared with the Airspace page's
    // own override field via the same project state.
    const [cnsOperationalAlt, setCnsOperationalAlt] = useState(state?.airspaceOperationalAltOverride ?? '');
    useEffect(() => {
        const t = setTimeout(() => {
            if (setState) setState(prev => ({ ...prev, airspaceOperationalAltOverride: cnsOperationalAlt }));
        }, 500);
        return () => clearTimeout(t);
    }, [cnsOperationalAlt]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (mapInst.current) {
            setTimeout(() => { mapInst.current?.invalidateSize(); }, 200);
        }
    }, [resultsCollapsed, resultsPoppedOut]);

    const obsWithCoords = useMemo(() => obstacles.filter(o => o.latitude && o.longitude), [obstacles]);

    // dirKeys must be checked BEFORE omniKeys to prevent 'ILS DME' matching the bare 'DME' omni key
    const dirKeys = ["ILS LOC", "ILS GP", "ILS DME", "VORTAC"];
    const omniKeys = ["CVOR", "VOR", "DVOR", "DF", "DIRECTION", "MARKER", "NDB", "GBAS", "VDB", "PSR", "SSR", "VHF", "DME"];
    const validCns = React.useMemo(() => {
        const unique = {};
        cns.forEach(c => {
            const typ = (c.feattype || c.type || "").toUpperCase();
            // Check directional first so 'ILS DME' doesn't accidentally match 'DME' in omniKeys
            if (dirKeys.some(k => typ.startsWith(k)) || omniKeys.some(k => typ === k || (typ.startsWith(k) && !typ.startsWith('ILS')))) {
                const key = (c.ident || c.name) + "_" + typ;
                unique[key] = c;
            }
        });
        return Object.values(unique);
    }, [cns]);

    // Most critical obstacle project-wide: the one with the single highest
    // penetration value against any visible CNS facility. This is the sane
    // default to land on (nobody should have to click through every obstacle
    // to find the worst BRA violation) — an explicit map/list selection
    // (selObstId) always overrides it.
    const mostCriticalObstacle = useMemo(() => {
        let best = null, bestPen = -Infinity;
        obsWithCoords.forEach(obs => {
            validCns.forEach(c => {
                const key = c.navaidid || c.ident;
                if (visible[key] === false) return;
                const fkey = FEATTYPE_TO_FACILITY_KEY[(c.feattype || '').toUpperCase()];
                const isDirectional = fkey && NAV_DIR[fkey] && c.heading_deg != null && c.A_m != null;
                const res = isDirectional ? computeDirectionalBra(c, obs) : computeBRA(c, obs);
                if (!res) return;
                const pen = parseFloat(res.penetration);
                if (Number.isNaN(pen)) return;
                if (pen > bestPen) { bestPen = pen; best = obs; }
            });
        });
        return best;
    }, [obsWithCoords, validCns, visible]);

    const selectedObstacle = obsWithCoords.find(o => o.id === selObstId) || mostCriticalObstacle || obsWithCoords[0];

    useEffect(() => {
        const init = {};
        validCns.forEach(c => {
            const typ = (c.feattype || c.type || "").toUpperCase();
            init[(c.ident || c.name) + "_" + typ] = true;
        });
        setVisible(init);
    }, [validCns]);

    useEffect(() => {
        if (!mapRef.current || mapInst.current) return;
        mapInst.current = L.map(mapRef.current, {
            center: [-28.5, 25.0],
            zoom: 6,
            zoomDelta: 0.7,
            zoomSnap: 0.7
        });
        tileRef.current = L.tileLayer(TILE_LAYERS.street.url, { attribution: TILE_LAYERS.street.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
        setTimeout(() => { mapInst.current?.invalidateSize(); }, 0);
        setMapState(mapInst.current);
        onMapReady?.(mapInst.current);
        return () => { mapInst.current?.remove(); mapInst.current = null; setMapState(null); onMapReady?.(null); };
    }, []);

    useEffect(() => {
        if (!mapInst.current) return;
        if (tileRef.current) mapInst.current.removeLayer(tileRef.current);
        const l = TILE_LAYERS[mapMode];
        tileRef.current = L.tileLayer(l.url, { attribution: l.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    // ── Shielding: real per-facility BRA check ──────────────────────────────
    // CNS/BRA's own project-obstacle assessment already runs entirely
    // client-side per (facility, obstacle) via computeBRA/computeDirectionalBra
    // — reuse those exact same functions on shielding candidates so this can
    // never diverge from what the real assessment says about a given point,
    // the same principle the other assessment pages' shielding checks follow.
    const comboShieldLayerRef = useRef(null);
    const cnsTrueShieldLayerRef = useRef(null);
    const shieldLinesLayerRef = useRef(null);
    const highestElevLinesLayerRef = useRef(null);
    const ctrLayerRef = useRef(null);

    // Fetch the aerodrome's CTR polygon on demand, only while the toggle is on.
    useEffect(() => {
        if (!showCtr || !project?.aerodrome) { setCtrData(null); return; }
        let cancelled = false;
        setCtrError('');
        getCtrByIcao(project.aerodrome)
            .then(({ data }) => { if (!cancelled) setCtrData(data); })
            .catch(e => { if (!cancelled) { setCtrData(null); setCtrError(e.response?.data?.detail || e.message); } });
        return () => { cancelled = true; };
    }, [showCtr, project?.aerodrome]);

    const isDirectionalCns = useCallback((c) => {
        const fkey = FEATTYPE_TO_FACILITY_KEY[(c.feattype || '').toUpperCase()];
        return !!(fkey && NAV_DIR[fkey] && c.heading_deg != null && c.A_m != null);
    }, []);

    // Every (project obstacle, facility) pair where that obstacle actually
    // penetrates that facility's BRA — not just the single worst obstacle per
    // facility (cnsSurfaceImpacts, below) but every penetrating obstacle
    // against every facility it individually penetrates. Feeds the
    // "lines to penetrating obstacles" map overlay so a line is only ever
    // drawn to the navaid(s) a given obstacle actually violates.
    const penetratingObstacleFacilityPairs = useMemo(() => {
        const pairs = [];
        obsWithCoords.forEach(o => {
            validCns.forEach(c => {
                const isDirectional = isDirectionalCns(c);
                const res = isDirectional ? computeDirectionalBra(c, o) : computeBRA(c, o);
                if (res && (res.outcome === 'penetrated' || res.outcome === 'CNS Engineer')) {
                    pairs.push({
                        obstacle: o, facility: c,
                        facilityIdent: c.ident || c.name,
                        penetration: res.penetration != null ? parseFloat(res.penetration) : null,
                        isEngineerZone: res.outcome === 'CNS Engineer',
                        surface: res.surface,
                        distM: res.distM,
                        vAngle: computeVerticalAngleDeg(c, o),
                    });
                }
            });
        });
        return pairs;
    }, [obsWithCoords, validCns, isDirectionalCns]);

    // For every penetrating (obstacle, facility) pair, ask the backend: does
    // this obstacle's own masking angle (curvature-corrected, 4/3-earth model)
    // stay clear at the aerodrome's CTR boundary at the operational floor
    // (the CTR's own published floor, or the operational altitude override
    // set on the Airspace page — e.g. a circuit altitude)? Piggybacks on the
    // "Lines: penetrating obstacles → navaids" toggle since it's the same
    // pair set, rather than adding a second checkbox for the same thing.
    const [maskingAngleResults, setMaskingAngleResults] = useState(new Map());
    const [maskingAngleLoading, setMaskingAngleLoading] = useState(false);
    useEffect(() => {
        if (!showHighestElevLines || !project?.aerodrome || penetratingObstacleFacilityPairs.length === 0) {
            setMaskingAngleResults(new Map());
            return;
        }
        let cancelled = false;
        setMaskingAngleLoading(true);
        Promise.all(penetratingObstacleFacilityPairs.map(async ({ obstacle: o, facility: c, facilityIdent }) => {
            const cLat = c.lat_cns || c.lat, cLon = c.lon_cns || c.lon;
            const cElev = parseFloat(c.elev_m ?? c.navaidelev ?? 0);
            if (!cLat || !cLon) return null;
            try {
                const { data } = await getCtrMaskingAngle({
                    navaid_lat: cLat, navaid_lon: cLon, navaid_elev_m: cElev,
                    obstacle_lat: o.latitude, obstacle_lon: o.longitude,
                    obstacle_elev_m: parseFloat(o.elevation_m || 0),
                    icao: project.aerodrome,
                    operational_alt_override_ft: cnsOperationalAlt !== '' && cnsOperationalAlt != null ? cnsOperationalAlt : undefined,
                });
                return { key: `${facilityIdent}::${o.id}`, facilityIdent, obstacle: o, data };
            } catch (e) {
                return { key: `${facilityIdent}::${o.id}`, facilityIdent, obstacle: o, error: e.response?.data?.detail || e.message };
            }
        })).then(results => {
            if (cancelled) return;
            // Keep only the single worst (least clearance) obstacle per
            // facility — that's the binding constraint; every other
            // penetrating obstacle on the same facility only clutters the
            // panel without changing which one actually needs attention.
            const byFacility = new Map();
            results.forEach(r => {
                if (!r) return;
                const cur = byFacility.get(r.facilityIdent);
                if (!cur || (cur.error && !r.error) ||
                    (!cur.error && !r.error && r.data.clearance_at_obstacle_ft < cur.data.clearance_at_obstacle_ft)) {
                    byFacility.set(r.facilityIdent, r);
                }
            });
            const map = new Map();
            byFacility.forEach(r => map.set(r.key, r));
            setMaskingAngleResults(map);
        }).finally(() => { if (!cancelled) setMaskingAngleLoading(false); });
        return () => { cancelled = true; };
    }, [showHighestElevLines, penetratingObstacleFacilityPairs, project?.aerodrome, cnsOperationalAlt]);

    // Which facilities does the PROJECT itself already penetrate, and by how
    // much? `worstPenetration` is null for the zero-tolerance zones (r-cylinder
    // / ILS ground plane) — those have no margin, ANY height is a violation.
    const cnsSurfaceImpacts = useMemo(() => {
        const out = {}; // facilityIdent -> { worstPenetration, isEngineerZone, surface, distM, vAngle, obstacle }
        validCns.forEach(c => {
            const isDirectional = isDirectionalCns(c);
            (obstacles || []).forEach(o => {
                if (!o.latitude || !o.longitude) return;
                const res = isDirectional ? computeDirectionalBra(c, o) : computeBRA(c, o);
                if (!res || (res.outcome !== 'penetrated' && res.outcome !== 'CNS Engineer')) return;
                const key = c.ident || c.name;
                const pen = res.penetration != null ? parseFloat(res.penetration) : null;
                const cur = out[key];
                const isEngineerZone = res.outcome === 'CNS Engineer';
                if (!cur || isEngineerZone || (cur.worstPenetration != null && pen != null && pen > cur.worstPenetration)) {
                    out[key] = {
                        worstPenetration: pen, isEngineerZone, surface: res.surface,
                        distM: res.distM, vAngle: computeVerticalAngleDeg(c, o), obstacle: o,
                    };
                }
            });
        });
        return out;
    }, [validCns, obstacles, isDirectionalCns]);
    const hasCnsImpact = Object.keys(cnsSurfaceImpacts).length > 0;

    // For every facility the project impacts, run every shielding candidate
    // through the same real BRA math (pure JS — no network round trip needed).
    const cnsShieldPens = useMemo(() => {
        if (!shieldOn || !shieldData || !hasCnsImpact) return {};
        const candidates = [
            ...(shieldData.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
            ...(shieldData.sof || []).map(r => ({ ...r, kind: 'SOF' })),
        ].filter(r => r.lat != null && r.lon != null);
        if (!candidates.length) return {};

        const pseudoObstacles = candidates.map(r => ({
            id: `shield_${r.kind}_${r.ogc_fid}`,
            latitude: r.lat, longitude: r.lon,
            elevation_m: parseFloat(r.elev_m) || 0,
            v_tolerance: parseFloat(r.v_tolerance_m) || 0,
            h_tolerance: 0,
            // Kept separate from h_tolerance above (which feeds the BRA
            // penetration math itself and stays untouched) — this is only the
            // candidate's own surveyed horizontal accuracy, used to size the
            // "on line to navaid" corridor below.
            h_tolerance_survey: parseFloat(r.h_tolerance_m) || 0,
            obstacle_type: r.type || '',
        }));

        const perFacility = {};
        Object.keys(cnsSurfaceImpacts).forEach(facilityIdent => {
            const c = validCns.find(v => (v.ident || v.name) === facilityIdent);
            if (!c) return;
            const isDirectional = isDirectionalCns(c);
            // Every project obstacle that actually penetrates THIS facility —
            // not just the single worst one — so a candidate is checked (and,
            // if requested, a line drawn) against each specific obstacle it
            // could realistically be shielding, on this navaid only.
            const penObstaclesForFacility = penetratingObstacleFacilityPairs.filter(p => p.facilityIdent === facilityIdent);
            perFacility[facilityIdent] = pseudoObstacles
                .map(o => {
                    const res = isDirectional ? computeDirectionalBra(c, o) : computeBRA(c, o);
                    if (!res) return null;
                    const lineTol = o.h_tolerance_survey > 0 ? o.h_tolerance_survey : undefined;
                    const obstacleAligns = penObstaclesForFacility.map(p => ({
                        obstacleId: p.obstacle.id,
                        obstacleName: p.obstacle.name,
                        penetration: p.penetration,
                        isEngineerZone: p.isEngineerZone,
                        distM: p.distM,
                        vAngle: p.vAngle,
                        latitude: p.obstacle.latitude,
                        longitude: p.obstacle.longitude,
                        align: computeLineAlignment(c, p.obstacle, o, lineTol),
                    }));
                    return { obstacle_id: o.id, ...res, vAngle: computeVerticalAngleDeg(c, o), obstacleAligns };
                })
                .filter(Boolean);
        });
        return perFacility;
    }, [shieldOn, shieldData, hasCnsImpact, cnsSurfaceImpacts, validCns, isDirectionalCns, penetratingObstacleFacilityPairs]);

    // Candidate pseudo-id -> [{ facility, surface, penetration, projectPenetration,
    // obstacleName }, ...] — evaluated per SPECIFIC penetrating project obstacle,
    // not just the single worst one per facility: "true shield" now means the
    // candidate already penetrates the same facility at least as badly as that
    // particular obstacle, is (for a sloping surface) closer to the facility than
    // it, and — when required — sits on the line between the facility and it.
    const cnsShieldVerdicts = useMemo(() => {
        const out = new Map();
        Object.entries(cnsShieldPens).forEach(([facilityIdent, rows]) => {
            rows.forEach(r => {
                if (r.outcome !== 'penetrated' && r.outcome !== 'CNS Engineer') return;
                (r.obstacleAligns || []).forEach(oa => {
                    const isTrueShield = oa.isEngineerZone
                        ? r.outcome === 'CNS Engineer'
                        : (r.penetration != null && oa.penetration != null && parseFloat(r.penetration) >= oa.penetration);
                    if (!isTrueShield) return;
                    // On a sloping surface (R-cone / Dir - Sloping), a candidate can only
                    // genuinely stand in front of this specific obstacle's line of sight
                    // if it sits closer to the facility than that obstacle does — farther
                    // out, it's independently penetrating the same surface, not shielding it.
                    if (isSlopingBraSurface(r.surface) && oa.distM != null && r.distM != null && r.distM >= oa.distM) return;
                    // Optional stricter gate: the candidate must actually sit on the
                    // straight line between the facility and this specific obstacle to
                    // count as a true shield, not merely closer/taller in isolation.
                    if (requireOnLine && !(oa.align && oa.align.onLine)) return;
                    const list = out.get(r.obstacle_id) || [];
                    list.push({
                        facility: facilityIdent, surface: r.surface,
                        penetration: r.penetration != null ? parseFloat(r.penetration) : null,
                        projectPenetration: oa.penetration,
                        vAngle: r.vAngle, projectVAngle: oa.vAngle,
                        align: oa.align, obstacleName: oa.obstacleName,
                    });
                    out.set(r.obstacle_id, list);
                });
            });
        });
        return out;
    }, [cnsShieldPens, requireOnLine]);

    // Candidate pseudo-id -> true if it falls under ANY BRA surface footprint
    // at all (not just "outside surfaces"), regardless of whether it penetrates.
    const cnsShieldInsideSurface = useMemo(() => {
        const out = new Map();
        Object.values(cnsShieldPens).forEach(rows => {
            rows.forEach(r => { if (r.outcome !== 'outside surfaces') out.set(r.obstacle_id, true); });
        });
        return out;
    }, [cnsShieldPens]);

    // Candidate pseudo-id -> [{ facility, surface, penetration }, ...] for
    // every facility it genuinely penetrates at its own position — independent
    // of whether it's also taller than the project's own worst point (the
    // red/grey flat-elevation check) or bad enough to count as a "true
    // shield" above. A shorter obstacle that still breaches a facility's cone
    // here is real evidence the flat elevation comparison misses entirely.
    const cnsShieldRealPenetration = useMemo(() => {
        const out = new Map();
        Object.entries(cnsShieldPens).forEach(([facilityIdent, rows]) => {
            rows.forEach(r => {
                if (r.outcome !== 'penetrated' && r.outcome !== 'CNS Engineer') return;
                const list = out.get(r.obstacle_id) || [];
                list.push({
                    facility: facilityIdent, surface: r.surface,
                    penetration: r.penetration != null ? parseFloat(r.penetration) : null,
                    vAngle: r.vAngle,
                    obstacleAligns: r.obstacleAligns || [],
                });
                out.set(r.obstacle_id, list);
            });
        });
        return out;
    }, [cnsShieldPens]);

    // Enrich each shield marker's own popup (from useShield) with the real
    // per-facility verdict, and recolour it yellow when it genuinely
    // penetrates a real BRA surface but wasn't already red (raw-elevation
    // shield) — a third state alongside the existing red/grey. When both are
    // true at once, a half-red/half-yellow halo marks it instead of one
    // silently winning.
    useEffect(() => {
        const group = shieldLayerRef.current;
        if (comboShieldLayerRef.current) { mapInst.current?.removeLayer(comboShieldLayerRef.current); comboShieldLayerRef.current = null; }
        if (!group || !hasCnsImpact) return;
        const comboGroup = L.featureGroup();
        group.eachLayer(layer => {
            if (!layer._shieldKey || !layer._basePopupHtml) return;
            const verdicts = cnsShieldVerdicts.get(layer._shieldKey);
            const insideAnySurface = cnsShieldInsideSurface.get(layer._shieldKey) || false;
            const realPenList = cnsShieldRealPenetration.get(layer._shieldKey) || [];
            const realPenetration = realPenList.length > 0;
            const onLineLabel = a => a == null ? 'unknown' : (a.onLine ? `Yes, offset ${a.lateralOffsetM.toFixed(0)} m (tol ${a.toleranceM.toFixed(0)} m)` : (a.betweenNavaidAndObstacle ? `No, offset ${a.lateralOffsetM.toFixed(0)} m (tol ${a.toleranceM.toFixed(0)} m)` : 'No, not between facility and obstacle'));
            const alignsSummary = aligns => (aligns && aligns.length)
                ? aligns.map(oa => `${oa.obstacleName || 'obstacle'}: ${onLineLabel(oa.align)}`).join('; ')
                : 'no penetrating project obstacle on this facility';
            const realPenNames = [...new Set(realPenList.map(v => `${v.facility} (${v.surface}, ∠${v.vAngle != null ? v.vAngle.toFixed(2) : '—'}°) — on line: [${alignsSummary(v.obstacleAligns)}]`))];
            const outsideLine = `<br/><span style="color:#8a94a6">Outside BRA surfaces?: ${insideAnySurface ? 'No' : 'Yes'}</span>`;
            const penetratesLine = realPenetration
                ? `<br/><span style="color:#2D9CDB">Penetrates real BRA surface (regardless of height)?: Yes — ${realPenNames.join(', ')}</span>`
                : `<br/><span style="color:#8a94a6">Penetrates real BRA surface (regardless of height)?: No</span>`;
            const surfacesLine = (verdicts && verdicts.length)
                ? verdicts.map(v =>
                    `<br/><b style="color:#FFB020">CNS BRA true shield: ${v.facility} — ${v.surface}</b> (shields ${v.obstacleName || 'project obstacle'}) ` +
                    (v.penetration != null
                        ? `(this obstacle penetrates by ${v.penetration.toFixed(1)} m vs ${v.obstacleName || 'that obstacle'}'s ${v.projectPenetration.toFixed(1)} m)`
                        : `(zero-tolerance zone — any height is a violation, same as the project)`) +
                    (v.vAngle != null && v.projectVAngle != null
                        ? ` — vertical angle ∠${v.vAngle.toFixed(2)}° vs obstacle's ∠${v.projectVAngle.toFixed(2)}°`
                        : '') +
                    ` — on line from project obstacle to navaid: ${onLineLabel(v.align)}`
                  ).join('')
                : `<br/><span style="color:#8a94a6">CNS BRA true shield: No</span>`;
            layer.setPopupContent(layer._basePopupHtml + outsideLine + penetratesLine + surfacesLine);
            if (realPenetration && !layer._shieldsRaw) {
                layer.setStyle({ color: '#FFE14D', fillColor: '#FFE14D', weight: 2, radius: 7, fillOpacity: 0.85 });
            } else if (realPenetration && layer._shieldsRaw) {
                const halo = L.marker(layer.getLatLng(), { icon: getShieldComboIcon(L), interactive: false });
                comboGroup.addLayer(halo);
            }
        });
        if (comboGroup.getLayers().length > 0) {
            comboShieldLayerRef.current = comboGroup;
            comboGroup.addTo(mapInst.current);
        }
    }, [shieldLayerRef, cnsShieldVerdicts, cnsShieldInsideSurface, cnsShieldRealPenetration, hasCnsImpact, shieldData, shieldOn]);

    // Distinct marker for every candidate confirmed to already penetrate a
    // facility as badly as (or worse than) the project — separate layer from
    // useShield's own generic markers, so the "not a new impact" verdict is
    // visually unambiguous rather than just recolouring the existing marker.
    useEffect(() => {
        if (!mapInst.current) return;
        if (cnsTrueShieldLayerRef.current) { mapInst.current.removeLayer(cnsTrueShieldLayerRef.current); cnsTrueShieldLayerRef.current = null; }
        if (!shieldOn || cnsShieldVerdicts.size === 0) return;

        const group = L.featureGroup();
        const byId = new Map();
        [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
         ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
            byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
        });

        cnsShieldVerdicts.forEach((verdicts, obsId) => {
            const r = byId.get(obsId);
            if (!r || r.lat == null || r.lon == null) return;
            const marker = L.circleMarker([r.lat, r.lon], {
                radius: 10, color: '#FFB020', weight: 3, fillColor: '#FFB020', fillOpacity: 0.35,
            });
            const label = r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type);
            const onLineLabel = a => a == null ? 'unknown' : (a.onLine ? `Yes, offset ${a.lateralOffsetM.toFixed(0)} m (tol ${a.toleranceM.toFixed(0)} m)` : (a.betweenNavaidAndObstacle ? `No, offset ${a.lateralOffsetM.toFixed(0)} m (tol ${a.toleranceM.toFixed(0)} m)` : 'No, not between facility and obstacle'));
            const lines = verdicts.map(v =>
                `<b>${v.facility}</b> — ${v.surface} — shields <b>${v.obstacleName || 'project obstacle'}</b> — ` +
                (v.penetration != null
                    ? `penetrates by <b>${v.penetration.toFixed(1)} m</b> (that obstacle: ${v.projectPenetration.toFixed(1)} m)`
                    : `zero-tolerance zone (same as project)`) +
                (v.vAngle != null && v.projectVAngle != null
                    ? `<br/>∠ this obstacle ${v.vAngle.toFixed(2)}° vs project obstacle ${v.projectVAngle.toFixed(2)}°`
                    : '') +
                `<br/>On line from project obstacle to navaid?: ${onLineLabel(v.align)}`
            ).join('<br/>');
            marker.bindPopup(
                `<b>📡 TRUE SHIELD</b><br/>${r.kind}: ${label}<br/>ID: ${r.caa_id || '—'} (row ${r.ogc_fid})<br/>${lines}<br/>` +
                `<span style="color:#FFB020">Already penetrates as much or more — the project is not a new impact from ${verdicts.length > 1 ? 'these obstacles' : 'this obstacle'}.</span>`
            );
            group.addLayer(marker);
        });

        if (group.getLayers().length > 0) {
            cnsTrueShieldLayerRef.current = group;
            group.addTo(mapInst.current);
        }
    }, [shieldOn, cnsShieldVerdicts, shieldData]);

    // Optional visual aid: draw the actual navaid → project-obstacle reference
    // line for every SPECIFIC (facility, penetrating obstacle) pair (dashed
    // gold), plus a line from that navaid to every shielding candidate that
    // penetrates it, coloured by whether the candidate is on line to that
    // specific obstacle — so the alignment math above can be checked by eye.
    // A line only ever appears for a navaid a project obstacle actually
    // penetrates, and only between that navaid and that obstacle/candidate.
    useEffect(() => {
        if (!mapInst.current) return;
        if (shieldLinesLayerRef.current) { mapInst.current.removeLayer(shieldLinesLayerRef.current); shieldLinesLayerRef.current = null; }
        if (!shieldOn || !showShieldLines || cnsShieldRealPenetration.size === 0) return;

        const byId = new Map();
        [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
         ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
            byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
        });

        const group = L.featureGroup();
        const drawnRefLines = new Set();

        cnsShieldRealPenetration.forEach((penList, obsId) => {
            const cand = byId.get(obsId);
            if (!cand || cand.lat == null || cand.lon == null) return;
            penList.forEach(v => {
                const c = validCns.find(vv => (vv.ident || vv.name) === v.facility);
                if (!c) return;
                const cLat = c.lat_cns || c.lat, cLon = c.lon_cns || c.lon;
                if (!cLat || !cLon) return;

                (v.obstacleAligns || []).forEach(oa => {
                    const refKey = `${v.facility}::${oa.obstacleId}`;
                    if (!drawnRefLines.has(refKey)) {
                        drawnRefLines.add(refKey);
                        L.polyline([[cLat, cLon], [oa.latitude, oa.longitude]], {
                            color: '#FFB020', weight: 2, dashArray: '6,4', opacity: 0.9,
                        }).bindTooltip(`${v.facility} → ${oa.obstacleName || 'project obstacle'} (reference line)`).addTo(group);
                    }

                    const onLine = oa.align?.onLine;
                    const label = cand.kind === 'SWOT' ? (cand.caa_id || cand.type) : (cand.site_name || cand.type);
                    L.polyline([[cLat, cLon], [cand.lat, cand.lon]], {
                        color: onLine ? '#32B464' : '#e84040', weight: 1.5, opacity: 0.75,
                    }).bindTooltip(
                        `${v.facility} → ${label} vs ${oa.obstacleName || 'obstacle'} — ${onLine ? 'on line' : 'off line'}` +
                        (oa.align ? ` (offset ${oa.align.lateralOffsetM.toFixed(0)} m, tol ${oa.align.toleranceM.toFixed(0)} m)` : '')
                    ).addTo(group);
                });
            });
        });

        if (group.getLayers().length > 0) {
            shieldLinesLayerRef.current = group;
            group.addTo(mapInst.current);
        }
    }, [shieldOn, showShieldLines, cnsShieldRealPenetration, validCns, shieldData]);

    // Independent visual aid: a line from each project obstacle that actually
    // penetrates a facility's BRA, straight to that specific facility only —
    // never to a navaid the obstacle doesn't penetrate, and never for an
    // obstacle that penetrates nothing at all. Regardless of shielding state.
    useEffect(() => {
        if (!mapInst.current) return;
        if (highestElevLinesLayerRef.current) { mapInst.current.removeLayer(highestElevLinesLayerRef.current); highestElevLinesLayerRef.current = null; }
        if (!showHighestElevLines || penetratingObstacleFacilityPairs.length === 0) return;

        const group = L.featureGroup();
        penetratingObstacleFacilityPairs.forEach(({ obstacle: o, facility: c, facilityIdent }) => {
            const cLat = c.lat_cns || c.lat, cLon = c.lon_cns || c.lon;
            if (!cLat || !cLon) return;
            const totElev = (parseFloat(o.elevation_m) || 0) + (parseFloat(o.v_tolerance) || 0);
            L.polyline([[cLat, cLon], [o.latitude, o.longitude]], {
                color: '#7B61FF', weight: 2, dashArray: '2,8', opacity: 0.9,
            }).bindTooltip(`${c.ident || c.name} → ${o.name || 'obstacle'} (${totElev.toFixed(1)} m) — penetrates`).addTo(group);

            // Extend the same line out to where it crosses the CTR boundary,
            // once the masking-angle result for this pair has come back.
            const mr = maskingAngleResults.get(`${facilityIdent}::${o.id}`);
            if (mr && !mr.error) {
                L.polyline([[o.latitude, o.longitude], [mr.data.boundary_lat, mr.data.boundary_lon]], {
                    color: '#7B61FF', weight: 1.5, dashArray: '1,6', opacity: 0.55,
                }).bindTooltip(
                    `${facilityIdent} → ${mr.data.ctr_designator} boundary — masking angle ${mr.data.masking_angle_deg}°, ` +
                    `${mr.data.masked_altitude_at_boundary_ft.toFixed(0)} ft AMSL at the edge ` +
                    `(${mr.data.shadowed_at_floor ? 'shadows' : 'clear of'} the ${mr.data.floor_ft_amsl.toFixed(0)} ft floor)`
                ).addTo(group);
                L.circleMarker([mr.data.boundary_lat, mr.data.boundary_lon], {
                    radius: 5, color: '#7B61FF', weight: 2, fillColor: mr.data.shadowed_at_floor ? '#e84040' : '#32B464', fillOpacity: 0.9,
                }).bindTooltip(`${mr.data.ctr_designator} boundary`).addTo(group);
            }
        });

        if (group.getLayers().length > 0) {
            highestElevLinesLayerRef.current = group;
            group.addTo(mapInst.current);
        }
    }, [showHighestElevLines, penetratingObstacleFacilityPairs, maskingAngleResults]);

    // Draw the aerodrome's CTR boundary itself, so the masking-angle lines
    // above can be checked visually against the real polygon they're aimed at.
    useEffect(() => {
        if (!mapInst.current) return;
        if (ctrLayerRef.current) { mapInst.current.removeLayer(ctrLayerRef.current); ctrLayerRef.current = null; }
        if (!showCtr || !ctrData?.geometry) return;

        const upper = formatAirspaceLimit(ctrData.upper_limit, ctrData.upper_limit_uom, ctrData.upper_limit_ref);
        const lower = formatAirspaceLimit(ctrData.lower_limit, ctrData.lower_limit_uom, ctrData.lower_limit_ref);
        const layer = L.geoJSON(ctrData.geometry, {
            style: { color: '#3AA8C4', weight: 2, fillColor: '#3AA8C4', fillOpacity: 0.06, dashArray: '4,4' },
        }).bindTooltip(ctrData.name || 'CTR', { sticky: true })
          .bindPopup(`<b>${ctrData.name}</b><br/><span style="font-size:11px">Upper: ${upper}<br/>Lower: ${lower}${ctrData.frequency ? `<br/>${ctrData.frequency}` : ''}</span>`)
          .addTo(mapInst.current);
        ctrLayerRef.current = layer;
    }, [showCtr, ctrData]);

    useEffect(() => {
        if (!mapInst.current || loading) return;
        layersRef.current.forEach(l => mapInst.current.removeLayer(l));
        layersRef.current = [];
        const bounds = [];

        validCns.forEach(c => {
            if (!c.lat || !c.lon) return;
            const typ = (c.feattype || c.type || "").toUpperCase();
            const key = (c.ident || c.name) + "_" + typ;
            const isOn = visible[key] !== false;
            const ll = [c.lat, c.lon]; bounds.push(ll);

            const iconUrl = cnsIconUrl(c.feattype || c.type);
            const m = iconUrl
                ? L.marker(ll, { icon: svgIcon(iconUrl, isOn ? 30 : 20), opacity: isOn ? 1 : 0.4 })
                : L.circleMarker(ll, { radius: 7, color: '#f5a623', fillColor: '#f5a623', fillOpacity: isOn ? 1 : 0.3, weight: 2 });
            m.bindPopup(`<b>${c.ident}</b><br/>${c.type || c.feattype}<br/>${c.name}<br/>${c.distance_km} km`);
            m.addTo(mapInst.current); layersRef.current.push(m);
            if (!isOn) return;

            const fkey = FEATTYPE_TO_FACILITY_KEY[(c.feattype || '').toUpperCase()];
            const hasAFixed = fkey && NAV_DIR[fkey] && NAV_DIR[fkey].A_fixed !== undefined;
            const ft = (c.feattype || '').toUpperCase();
            const isIlsDir = ft.startsWith('ILS') && c.heading_deg != null;
            const isDirectional = fkey && NAV_DIR[fkey] && c.heading_deg != null &&
                (c.A_m != null || hasAFixed || isIlsDir);

            const isIlsType = ft.startsWith('ILS');
            const drawHdg = isIlsType
                ? (c.heading_deg + 180) % 360
                : c.heading_deg;

            if (isDirectional) {
                const p = NAV_DIR[fkey];
                const b = p.b, D = p.D, H = p.H, h = p.h, sideL = p.L;

                const A = p.A_fixed !== undefined ? p.A_fixed
                    : (c.A_m != null ? c.A_m : (ft.startsWith('ILS LOC') ? 3000 : ft.startsWith('ILS DME') ? 900 : 0));
                const r = p.r_fixed !== undefined ? p.r_fixed : A + (p.r_extra || 0);

                const H_over_h_div_r = H * (r / h);
                const ix_x = A + H_over_h_div_r;

                const extend_to_circle = (start_x, start_y, dir_x, dir_y) => {
                    const dx = dir_x - start_x;
                    const dy = dir_y - start_y;
                    const A_q = dx * dx + dy * dy;
                    const B_q = 2 * (dx * start_x + dy * start_y);
                    const C_q = start_x * start_x + start_y * start_y - r * r;
                    const disc = B_q * B_q - 4 * A_q * C_q;
                    if (disc < 0) return [dir_x, dir_y];
                    const t1 = (-B_q + Math.sqrt(disc)) / (2 * A_q);
                    const t2 = (-B_q - Math.sqrt(disc)) / (2 * A_q);
                    const t = Math.max(t1, t2);
                    return [start_x + t * dx, start_y + t * dy];
                };

                const [right_int_x, right_int_y] = extend_to_circle(A, D, ix_x, sideL);
                const [left_int_x, left_int_y] = extend_to_circle(A, -D, ix_x, -sideL);

                if (visible[key + '_z1'] !== false) {
                    const z1 = [
                        localToGlobal(-b, -D, c.lat, c.lon, drawHdg),
                        localToGlobal(-b,  D, c.lat, c.lon, drawHdg),
                        localToGlobal(A,   D, c.lat, c.lon, drawHdg),
                        localToGlobal(A,  -D, c.lat, c.lon, drawHdg)
                    ];
                    layersRef.current.push(L.polygon(z1, { color: groundPlaneStyle.color, fillColor: groundPlaneStyle.color, fillOpacity: groundPlaneStyle.opacity, weight: 2 }).addTo(mapInst.current).bindTooltip('Zone 1'));
                }

                if (visible[key + '_z2'] !== false) {
                    const sr = [
                        localToGlobal(-b, D, c.lat, c.lon, drawHdg),
                        localToGlobal(A, D, c.lat, c.lon, drawHdg),
                        localToGlobal(ix_x, sideL, c.lat, c.lon, drawHdg),
                        localToGlobal(-b, sideL, c.lat, c.lon, drawHdg)
                    ];
                    layersRef.current.push(L.polygon(sr, { color: sideAreaStyle.color, fillColor: sideAreaStyle.color, fillOpacity: sideAreaStyle.opacity, weight: 1.5 }).addTo(mapInst.current).bindTooltip('Zone 2 Right'));

                    const sl = [
                        localToGlobal(-b, -D, c.lat, c.lon, drawHdg),
                        localToGlobal(A, -D, c.lat, c.lon, drawHdg),
                        localToGlobal(ix_x, -sideL, c.lat, c.lon, drawHdg),
                        localToGlobal(-b, -sideL, c.lat, c.lon, drawHdg)
                    ];
                    layersRef.current.push(L.polygon(sl, { color: sideAreaStyle.color, fillColor: sideAreaStyle.color, fillOpacity: sideAreaStyle.opacity, weight: 1.5 }).addTo(mapInst.current).bindTooltip('Zone 2 Left'));
                }

                if (visible[key + '_z3'] !== false) {
                    const buildArcPts = (rx1, ry1, rx2, ry2, radius) => {
                        let a1 = Math.atan2(ry1, rx1);
                        let a2 = Math.atan2(ry2, rx2);
                        if (a2 < a1) a2 += 2 * Math.PI;
                        const pts = [];
                        const steps = 30;
                        for (let i = 0; i <= steps; i++) {
                            const a = a1 + (a2 - a1) * (i / steps);
                            pts.push(localToGlobal(radius * Math.cos(a), radius * Math.sin(a), c.lat, c.lon, drawHdg));
                        }
                        return pts;
                    };
                    const arcPts = buildArcPts(left_int_x, left_int_y, right_int_x, right_int_y, r);

                    const z3 = [
                        localToGlobal(A, -D, c.lat, c.lon, drawHdg),
                        localToGlobal(ix_x, -sideL, c.lat, c.lon, drawHdg),
                        localToGlobal(left_int_x, left_int_y, c.lat, c.lon, drawHdg),
                        ...arcPts,
                        localToGlobal(right_int_x, right_int_y, c.lat, c.lon, drawHdg),
                        localToGlobal(ix_x, sideL, c.lat, c.lon, drawHdg),
                        localToGlobal(A, D, c.lat, c.lon, drawHdg)
                    ];
                    layersRef.current.push(L.polygon(z3, { color: slopingSurfaceStyle.color, fillColor: slopingSurfaceStyle.color, fillOpacity: slopingSurfaceStyle.opacity, weight: 1.5 }).addTo(mapInst.current).bindTooltip('Zone 3'));
                }

            } else {
                const p = getParams(c.type || c.feattype);
                const center = turf.point([c.lon, c.lat]);

                if (visible[key + '_rcyl'] !== false) {
                    const rCyl = turf.buffer(center, p.rCyl / 1000, { units: 'kilometers', steps: 64 });
                    const rCylPoly = L.geoJSON(rCyl, { color: rCylinderStyle.color, fillColor: rCylinderStyle.color, fillOpacity: rCylinderStyle.opacity, weight: 1.5 }).addTo(mapInst.current).bindTooltip('r-cylinder');
                    layersRef.current.push(rCylPoly);
                }

                if (visible[key + '_rcone'] !== false) {
                    const rCone = turf.buffer(center, p.rCone / 1000, { units: 'kilometers', steps: 64 });
                    const rConePoly = L.geoJSON(rCone, { color: rConeStyle.color, fillColor: rConeStyle.color, fillOpacity: rConeStyle.opacity, weight: 1.5 }).addTo(mapInst.current).bindTooltip('r-cone');
                    layersRef.current.push(rConePoly);
                }

                if (p.jCyl > 0 && visible[key + '_jcyl'] !== false) {
                    const jCyl = turf.buffer(center, p.jCyl / 1000, { units: 'kilometers', steps: 64 });
                    const jCylPoly = L.geoJSON(jCyl, { color: jCylinderStyle.color, fillColor: jCylinderStyle.color, fillOpacity: jCylinderStyle.opacity, weight: 1.5 }).addTo(mapInst.current).bindTooltip('j-cylinder');
                    layersRef.current.push(jCylPoly);
                }
            }
        });

        getObstaclesToRender(obsWithCoords, project?.id, showObstacles).forEach(o => {
            const ll = [o.latitude, o.longitude];
            bounds.push(ll);
            const isSel = o.id === selObstId;

            let isPenetrating = false;
            let isAssessed = false;
            let maxPen = -Infinity;
            let foundAny = false;
            let cnsEng = false;
            let surfaceStr = '—';

            for (const c of validCns) {
                const key = c.navaidid || c.ident;
                if (visible[key] === false) continue;
                const fkey = FEATTYPE_TO_FACILITY_KEY[(c.feattype || '').toUpperCase()];
                const isDirectional = fkey && NAV_DIR[fkey] && c.heading_deg != null && c.A_m != null;
                const res = isDirectional ? computeDirectionalBra(c, o) : computeBRA(c, o);
                if (res) {
                    isAssessed = true;
                    foundAny = true;
                    if (res.outcome === 'penetrated' || res.outcome === 'CNS Engineer') {
                        isPenetrating = true;
                    }
                    if (res.outcome === 'CNS Engineer' || res.penetration === 'N/A') {
                        cnsEng = true;
                    }
                    const val = parseFloat(res.penetration);
                    if (!isNaN(val) && val > maxPen) {
                        maxPen = val;
                        surfaceStr = `${c.ident || c.name} (${res.surface})`;
                    } else if (isNaN(val) && res.surface !== '—' && surfaceStr === '—') {
                        surfaceStr = `${c.ident || c.name} (${res.surface})`;
                    }
                }
            }

            let penetrationStr = '—';
            if (foundAny) {
                if (maxPen > 0) {
                    penetrationStr = `${maxPen.toFixed(2)} m`;
                } else if (cnsEng) {
                    penetrationStr = 'CNS Engineer';
                } else {
                    penetrationStr = `${maxPen.toFixed(2)} m (Clear)`;
                }
            }

            const outcome = isAssessed ? (isPenetrating ? 'penetrated' : 'clear') : undefined;
            const m = L.marker(ll, { icon: obsIcon(outcome, o, showObsLabels), zIndexOffset: isSel ? 100 : 0 })
                .bindPopup(`<b>${o.name}</b><br/>ID: ${o.id}<br/>ELEVATION : ${o.elevation_m ?? '—'} m<br/>SURFACE: ${surfaceStr}<br/>PENETRATION: ${penetrationStr}`)
                .on('click', () => setSelObstId(o.id));
            if (onObstacleContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onObstacleContextMenu(o, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(mapInst.current); layersRef.current.push(m);
        });
        addHullPolygonsToMap(mapInst.current, obstacles, project?.id, layersRef);

        if (bounds.length) mapInst.current.fitBounds(L.latLngBounds(bounds), { padding: [60, 60], maxZoom: 13 });
    }, [cns, arps, obstacles, loading, visible, selObstId, showObstacles, showObsLabels, showArpLabels, onObstacleContextMenu, groundPlaneStyle, sideAreaStyle, slopingSurfaceStyle, rCylinderStyle, rConeStyle, jCylinderStyle]); // eslint-disable-line

    const handleCapture = async () => {
        if (!mapInst.current || !project?.id) return;
        setCapturing(true);
        try {
            const container = mapRef.current;
            const containerRect = container.getBoundingClientRect();
            const w = Math.round(containerRect.width);
            const h = Math.round(containerRect.height);

            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#07111e';
            ctx.fillRect(0, 0, w, h);

            const tileEls = [...container.querySelectorAll('.leaflet-tile:not(.leaflet-tile-loading)')];
            await Promise.all(tileEls.map(async tile => {
                if (!tile.src) return;
                const tRect = tile.getBoundingClientRect();
                const tx = Math.floor(tRect.left - containerRect.left);
                const ty = Math.floor(tRect.top - containerRect.top);
                const tw = Math.ceil(tRect.width) + 1;
                const th = Math.ceil(tRect.height) + 1;
                try {
                    const resp = await fetch(tile.src, { mode: 'cors' });
                    if (!resp.ok) return;
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    await new Promise(resolve => {
                        const img = new Image();
                        img.onload = () => { ctx.drawImage(img, tx, ty, tw, th); URL.revokeObjectURL(url); resolve(); };
                        img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                        img.src = url;
                    });
                } catch {}
            }));

            const svgEl = container.querySelector('.leaflet-overlay-pane svg');
            if (svgEl) {
                const svgRect = svgEl.getBoundingClientRect();
                const sx = Math.round(svgRect.left - containerRect.left);
                const sy = Math.round(svgRect.top - containerRect.top);
                const clone = svgEl.cloneNode(true);
                clone.style.transform = ''; clone.style.left = '0'; clone.style.top = '0';
                const svgStr = new XMLSerializer().serializeToString(clone);
                const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                await new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => { ctx.drawImage(img, sx, sy, Math.round(svgRect.width), Math.round(svgRect.height)); URL.revokeObjectURL(url); resolve(); };
                    img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                    img.src = url;
                });
            }

            const markerEls = [...container.querySelectorAll('.leaflet-marker-icon')];
            for (const marker of markerEls) {
                const mRect = marker.getBoundingClientRect();
                const x = Math.round(mRect.left - containerRect.left);
                const y = Math.round(mRect.top - containerRect.top);
                const img = marker.querySelector('img');
                if (img && img.complete && img.naturalWidth) {
                    try { ctx.drawImage(img, x, y, Math.round(mRect.width), Math.round(mRect.height)); } catch {}
                }
            }

            const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
            await fetch(`${API_BASE}/cns/screenshot?project_id=${project.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ image: dataUrl }),
            });
        } catch (err) {
            console.error('Capture failed:', err);
        } finally {
            setCapturing(false);
        }
    };

    // Build the facility rows for a single obstacle, and flag the
    // "controlling" facility among them — the one whose BRA is most
    // restrictive for that obstacle (highest penetration value).
    const buildRowsForObstacle = (obstacle) => {
        const rows = [];
        validCns.forEach(c => {
            const key = c.navaidid || c.ident;
            if (visible[key] === false) return;
            if (!obstacle) return;

            const fkey = FEATTYPE_TO_FACILITY_KEY[(c.feattype || '').toUpperCase()];
            const isDirectional = fkey && NAV_DIR[fkey] && c.heading_deg != null && c.A_m != null;

            const res = isDirectional ? computeDirectionalBra(c, obstacle) : computeBRA(c, obstacle);

            if (res) {
                const vAngle = computeVerticalAngleDeg(c, obstacle);
                rows.push({
                    obsName: obstacle.name || '—',
                    obsElev: parseFloat(obstacle.elevation_m || 0).toFixed(2),
                    vTol: obstacle.v_tolerance || 0,
                    hTol: obstacle.h_tolerance || 0,
                    ident: c.ident || '—',
                    name: c.name || '—',
                    type: c.type || c.feattype || '—',
                    distM: res.distM ?? '—',
                    brng: res.brng != null ? res.brng : '—',
                    vAngle: vAngle != null ? vAngle.toFixed(2) : '—',
                    surface: res.surface,
                    braElev: res.braElev,
                    penetration: res.penetration,
                    outcome: res.outcome
                });
            }
        });

        let worst = null;
        rows.forEach(r => {
            const p = parseFloat(r.penetration);
            if (Number.isNaN(p)) return;
            if (!worst || p > parseFloat(worst.penetration)) worst = r;
        });
        if (worst) worst.isControlling = true;
        return rows;
    };

    // On-screen table: the currently selected obstacle only (defaults to the
    // most critical one project-wide, see mostCriticalObstacle above).
    const tableRows = buildRowsForObstacle(selectedObstacle);

    // Export dataset: every obstacle against every visible facility, each
    // obstacle's own controlling facility flagged — the full record for a
    // report, not just whichever obstacle happens to be selected on screen.
    const allObstacleRows = useMemo(() => {
        const rows = [];
        obsWithCoords.forEach(obs => { rows.push(...buildRowsForObstacle(obs)); });
        return rows;
    }, [obsWithCoords, validCns, visible]); // eslint-disable-line react-hooks/exhaustive-deps

    // BRA surfaces KMZ — sends the currently visible facilities (with their
    // already-resolved elev_m, same datum used on screen) plus project_id
    // (so the backend adds every project obstacle and their convex-hull
    // footprint/3D solid via the shared _obstacles_and_hull_kml helper every
    // other assessment page's KMZ export also uses). Shared by both the
    // file-download button and the in-app 3D preview below.
    const buildCnsBraKmzBlob = async (mode) => {
        const facilities = validCns
            .filter(c => visible[(c.navaidid || c.ident)] !== false)
            .map(c => ({
                lat: c.lat_cns || c.lat,
                lon: c.lon_cns || c.lon,
                elev_m: c.elev_m ?? c.navaidelev ?? 0,
                type: c.type,
                feattype: c.feattype,
                ident: c.ident,
                name: c.name,
                heading_deg: c.heading_deg,
                A_m: c.A_m,
            }))
            .filter(f => f.lat && f.lon);

        if (facilities.length === 0 && !project?.id) {
            throw new Error('No visible CNS facilities or obstacles to export.');
        }
        const response = await fetch(`${API_BASE}/cns/kml`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ project_name: project?.name || 'Project', mode, facilities, project_id: project?.id || null }),
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(`Failed to build KMZ: ${text}`);
        }
        return response.blob();
    };

    const handleDownloadCnsBraKmz = async (mode) => {
        try {
            const blob = await buildCnsBraKmzBlob(mode);
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `${(project?.name || 'Project').replace(/[\/\\]/g, '_')}_CNS_BRA_${mode.toUpperCase()}.kmz`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('CNS BRA KMZ export error:', err);
            alert('Failed to export KMZ: ' + err.message);
        }
    };

    const [show3DPreview, setShow3DPreview] = useState(false);

    const renderResultsContent = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                <div style={{ padding: '8px 16px', borderBottom: `1px solid ${T.border}33`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: T.blue }}>CNS / BRA Assessment Results</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {allObstacleRows.length > 0 && (
                        <button
                            title="Exports BRA results for every project obstacle against every visible facility, not just the one shown on screen"
                            onClick={() => {
                                const headers = [
                                    "Obstacle Name", "Obstacle Elevation (m)", "Vertical Tol (m)", "Horizontal Tol (m)",
                                    "CNS Ident", "Facility Name", "Facility Type", "Distance (m)", "Bearing (°T)", "Vertical Angle (°)", "Surface Name", "Surface Elevation (m)", "Penetration (m)", "Outcome", "Controlling"
                                ];
                                const rows = allObstacleRows.map(r => [
                                    r.obsName === '—' ? '-' : r.obsName,
                                    r.obsElev,
                                    r.vTol,
                                    r.hTol,
                                    r.ident === '—' ? '-' : r.ident,
                                    r.name === '—' ? '-' : r.name,
                                    r.type === '—' ? '-' : r.type,
                                    r.distM === '—' ? '-' : r.distM,
                                    r.brng === '—' ? '-' : r.brng,
                                    r.vAngle === '—' ? '-' : r.vAngle,
                                    r.surface === '—' ? '-' : r.surface,
                                    r.braElev === '—' ? '-' : r.braElev,
                                    r.penetration === '—' ? '-' : r.penetration,
                                    r.outcome,
                                    r.isControlling ? 'Yes' : ''
                                ]);
                                exportToCsv(headers, rows, `CNS_BRA_Assessment_Results.csv`);
                            }}
                            style={{
                                padding: '3px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                                background: T.blue, color: T.white, fontSize: 10, fontWeight: 700,
                                display: 'flex', alignItems: 'center', gap: 4
                            }}
                        >
                            📥 Export CSV (all obstacles)
                        </button>
                    )}
                    <button
                        onClick={() => handleDownloadCnsBraKmz('2d')}
                        title="Download 2D KMZ (Flat Footprints) of the visible BRA surfaces"
                        style={{
                            background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`,
                            borderRadius: 4, padding: '3px 8px', cursor: 'pointer',
                            color: T.muted, fontSize: 10, fontWeight: 700, marginLeft: 8
                        }}
                    >
                        KMZ (2D)
                    </button>
                    <button
                        onClick={() => handleDownloadCnsBraKmz('3d')}
                        title="Download 3D KMZ (True Altitudes) of the visible BRA surfaces"
                        style={{
                            background: 'rgba(58,138,196,0.15)', border: `1px solid ${T.blue}44`,
                            borderRadius: 4, padding: '3px 8px', cursor: 'pointer',
                            color: T.blue, fontSize: 10, fontWeight: 700, marginLeft: 4
                        }}
                    >
                        KMZ (3D)
                    </button>
                    <button
                        onClick={() => setShow3DPreview(true)}
                        title="Preview the visible BRA surfaces and project obstacles in 3D, right here — no Google Earth needed"
                        style={{
                            background: 'rgba(46,204,113,0.15)', border: `1px solid #2ECC7144`,
                            borderRadius: 4, padding: '3px 8px', cursor: 'pointer',
                            color: '#2ECC71', fontSize: 10, fontWeight: 700, marginLeft: 4
                        }}
                    >
                        🌐 3D Preview
                    </button>
                    </div>
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, whiteSpace: 'nowrap' }}>
                        <thead>
                            <tr style={{ background: T.bg0, position: 'sticky', top: 0, zIndex: 1 }}>
                                {[
                                    'obstacle name', 'obstacle elevation (m)', 'vertical tol (m)', 'horizontal tol (m)',
                                    'cns ident', 'name', 'type', 'distance (m)', 'bearing (°t)', 'vertical angle (°)', 'surface name', 'surface elevation (m)', 'Penetration (m)', 'outcome'
                                ].map(h => (
                                    <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: T.muted, fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tableRows.length > 0 ? tableRows.map((r, i) => (
                                <tr key={i} style={{ borderBottom: `1px solid ${T.border}22`, background: (r.outcome === 'penetrated') ? 'rgba(232,64,64,0.15)' : (r.outcome === 'not penetrated') ? 'rgba(50,180,100,0.10)' : (r.outcome === 'CNS Engineer') ? 'rgba(245,166,35,0.10)' : 'transparent' }}>
                                    <td style={{ padding: '5px 10px', color: T.white, fontWeight: 700 }}>{r.obsName}</td>
                                    <td style={{ padding: '5px 10px', color: T.white }}>{r.obsElev}</td>
                                    <td style={{ padding: '5px 10px', color: T.white }}>{r.vTol}</td>
                                    <td style={{ padding: '5px 10px', color: T.white }}>{r.hTol}</td>
                                    <td style={{ padding: '5px 10px', color: '#f5a623', fontWeight: 700 }}>
                                        {r.ident} {r.isControlling && <span style={{ color: T.blue, fontSize: 9 }}>[Controlling]</span>}
                                    </td>
                                    <td style={{ padding: '5px 10px', color: T.muted }}>{r.name.length > 15 ? r.name.substring(0, 15) + '...' : r.name}</td>
                                    <td style={{ padding: '5px 10px', color: T.white }}>{r.type}</td>
                                    <td style={{ padding: '5px 10px', color: T.white }}>{r.distM}</td>
                                    <td style={{ padding: '5px 10px', color: T.white }}>{r.brng !== '—' ? `${r.brng}°` : '—'}</td>
                                    <td style={{ padding: '5px 10px', color: T.white }}>{r.vAngle !== '—' ? `${r.vAngle}°` : '—'}</td>
                                    <td style={{ padding: '5px 10px', color: T.white }}>{r.surface}</td>
                                    <td style={{ padding: '5px 10px', color: T.white }}>{r.braElev}</td>
                                    <td style={{ padding: '5px 10px', color: T.white }}>{r.penetration}</td>
                                    <td style={{ padding: '5px 10px' }}><StatusBadge val={r.outcome} /></td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={14} style={{ padding: '20px 10px', textAlign: 'center', color: T.muted }}>No active assessment results</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            {/* Left Panel: Settings & Toggles */}
            <div style={{ width: 300, background: T.bg1, backdropFilter: 'blur(var(--blur-md))', WebkitBackdropFilter: 'blur(var(--blur-md))', borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                {/* Obstacle Selector */}
                <div style={{ padding: 16, borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, marginBottom: 8, letterSpacing: 0.5 }}>BRA AGAINST OBSTACLE</div>
                    <select
                        value={selObstId || ''} onChange={e => setSelObstId(e.target.value === '' ? null : Number(e.target.value))}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 6, background: T.bg0, border: `1px solid ${T.border}`, color: T.white, fontSize: 12, cursor: 'pointer' }}
                    >
                        {obsWithCoords.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                </div>

                <div style={{ padding: '10px 14px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: T.white }}>📡 CNS Facilities ({validCns.length})</div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11, color: T.muted }}>
                        <input type="checkbox" checked={validCns.length > 0 && validCns.every(c => {
                            const typ = (c.feattype || c.type || "").toUpperCase();
                            const key = (c.ident || c.name) + "_" + typ;
                            return visible[key] !== false;
                        })} onChange={(e) => {
                            const val = e.target.checked;
                            const next = { ...visible };
                            validCns.forEach(c => {
                                const typ = (c.feattype || c.type || "").toUpperCase();
                                const key = (c.ident || c.name) + "_" + typ;
                                next[key] = val;
                                const fkey = FEATTYPE_TO_FACILITY_KEY[(c.feattype || '').toUpperCase()];
                                const isDirectional = fkey && NAV_DIR[fkey] && c.heading_deg != null;
                                if (isDirectional) {
                                    ['_z1', '_z2', '_z3'].forEach(s => next[key + s] = val);
                                } else {
                                    ['_rcyl', '_rcone', '_jcyl'].forEach(s => next[key + s] = val);
                                }
                            });
                            setVisible(next);
                        }} style={{ accentColor: T.blue, cursor: 'pointer' }} />
                        All
                    </label>
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {validCns.length === 0 && <div style={{ padding: 16, fontSize: 12, color: T.muted }}>No omnidirectional NAVAIDs found.</div>}
                    {validCns.map((c, i) => {
                        const typ = (c.feattype || c.type || "").toUpperCase();
                        const key = (c.ident || c.name) + "_" + typ;
                        const isOn = visible[key] !== false;

                        const fkey = FEATTYPE_TO_FACILITY_KEY[(c.feattype || '').toUpperCase()];
                        const isDirectional = fkey && NAV_DIR[fkey] && c.heading_deg != null;

                        const toggleMain = () => {
                            const next = !isOn;
                            const update = { [key]: next };
                            if (isDirectional) {
                                ['_z1', '_z2', '_z3'].forEach(s => update[key + s] = next);
                            } else {
                                ['_rcyl', '_rcone', '_jcyl'].forEach(s => update[key + s] = next);
                            }
                            setVisible(v => ({ ...v, ...update }));
                        };

                        return (
                            <div key={i} style={{ borderBottom: `1px solid ${T.border}11` }}>
                                <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                    <input type="checkbox" checked={isOn} onChange={toggleMain} style={{ marginTop: 2, accentColor: '#f5a623', cursor: 'pointer' }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: isOn ? '#f5a623' : T.muted }}>{c.ident || '—'}</div>
                                        <div style={{ fontSize: 11, color: isOn ? T.white : T.muted }}>{c.name}</div>
                                        <div style={{ fontSize: 10, color: T.muted }}>{c.type} · {c.distance_km} km</div>

                                        {/* Sub-toggles */}
                                        {isOn && (
                                            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                {isDirectional ? [
                                                    { id: '_z1', l: 'Zone 1 (Ground)', c: groundPlaneStyle.color },
                                                    { id: '_z2', l: 'Zone 2 (Side)', c: sideAreaStyle.color },
                                                    { id: '_z3', l: 'Zone 3 (Sloping)', c: slopingSurfaceStyle.color }
                                                ].map(z => (
                                                    <label key={z.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                                        <input type="checkbox" checked={visible[key + z.id] !== false}
                                                            onChange={() => setVisible(v => ({ ...v, [key + z.id]: visible[key + z.id] === false }))}
                                                            style={{ width: 10, height: 10, accentColor: z.c }} />
                                                        <span style={{ fontSize: 9, color: T.muted }}>{z.l}</span>
                                                    </label>
                                                )) : [
                                                    { id: '_rcyl', l: 'r-cylinder', c: rCylinderStyle.color },
                                                    { id: '_rcone', l: 'R-cone', c: rConeStyle.color },
                                                    { id: '_jcyl', l: 'j-cylinder (WEF)', c: jCylinderStyle.color }
                                                ].map(z => (
                                                    <label key={z.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                                        <input type="checkbox" checked={visible[key + z.id] !== false}
                                                            onChange={() => setVisible(v => ({ ...v, [key + z.id]: visible[key + z.id] === false }))}
                                                            style={{ width: 10, height: 10, accentColor: z.c }} />
                                                        <span style={{ fontSize: 9, color: T.muted }}>{z.l}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {/* BRA Legend */}
                <div style={{ padding: '10px 14px', borderTop: `1px solid ${T.border}`, fontSize: 10 }}>
                    <div style={{ color: T.muted, marginBottom: 6, fontWeight: 700 }}>SURFACES</div>
                    {[{ c: rCylinderStyle.color, l: 'r-cylinder' }, { c: rConeStyle.color, l: 'R-cone' }, { c: jCylinderStyle.color, l: 'j-cylinder (WEF)' }].map(x => (
                        <div key={x.l} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                            <div style={{ width: 12, height: 3, background: x.c, borderRadius: 2 }} />
                            <span style={{ color: T.muted }}>{x.l}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right: map + table */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, display: 'flex', gap: 6, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 6, border: `1px solid ${T.border}`, backdropFilter: 'blur(8px)', alignItems: 'center' }}>
                    {Object.entries(TILE_LAYERS).map(([key, layer]) => (
                        <button key={key} onClick={() => setMapMode(key)} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', background: mapMode === key ? T.blue : 'transparent', color: mapMode === key ? T.white : T.muted, fontSize: 11, fontWeight: mapMode === key ? 700 : 400 }}>{layer.label}</button>
                    ))}
                    <div style={{ width: 1, background: T.border, height: 16, margin: '0 4px' }} />
                    <button 
                        onClick={handleCapture} 
                        disabled={capturing}
                        style={{ 
                            padding: '4px 12px', borderRadius: 6, border: 'none', cursor: capturing ? 'not-allowed' : 'pointer', 
                            background: T.safe, color: T.white, fontSize: 11, fontWeight: 700,
                            display: 'flex', alignItems: 'center', gap: 6
                        }}
                    >
                        {capturing ? '⌛ Saving...' : 'Capture Screenshot'}
                    </button>
                    <div style={{ width: 1, background: T.border, height: 16, margin: '0 4px' }} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11, color: T.white, whiteSpace: 'nowrap' }}>
                        <input type="checkbox" checked={showHighestElevLines} onChange={e => setShowHighestElevLines(e.target.checked)} style={{ accentColor: '#7B61FF', cursor: 'pointer' }} />
                        Lines: penetrating obstacles → navaids
                    </label>
                    <div style={{ width: 1, background: T.border, height: 16, margin: '0 4px' }} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11, color: T.white, whiteSpace: 'nowrap' }}>
                        <input type="checkbox" checked={showCtr} onChange={e => setShowCtr(e.target.checked)} style={{ accentColor: '#3AA8C4', cursor: 'pointer' }} />
                        Show CTR{ctrError ? ' (not found)' : ''}
                    </label>
                </div>
                <ShieldControl top={92} shieldOn={shieldOn} toggleShield={toggleShield} shieldData={shieldData} shieldLoading={shieldLoading} shieldError={shieldError} projectMaxTotalElev={projectMaxTotalElev} projectPenetratingMaxTotalElev={projectPenetratingMaxTotalElev} radiusKm={radiusKm} setRadiusKm={setRadiusKm} exportCsv={exportCsv}
                    penetratingCount={cnsShieldRealPenetration.size}
                    onExportPenetrating={() => {
                        const byId = new Map();
                        [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
                         ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
                            byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
                        });
                        const entries = [];
                        cnsShieldRealPenetration.forEach((penList, obsId) => {
                            const r = byId.get(obsId);
                            if (!r) return;
                            penList.forEach(v => {
                                const onLineFor = a => a == null ? 'unknown' : (a.onLine ? `Yes ${a.lateralOffsetM.toFixed(0)}m/tol${a.toleranceM.toFixed(0)}m` : (a.betweenNavaidAndObstacle ? `No ${a.lateralOffsetM.toFixed(0)}m/tol${a.toleranceM.toFixed(0)}m` : 'No (not between)'));
                                const alignsStr = (v.obstacleAligns || []).map(oa => `${oa.obstacleName || 'obstacle'}: ${onLineFor(oa.align)}`).join(' | ') || 'no penetrating project obstacle';
                                entries.push({
                                    r,
                                    surfaceLabel: `${v.facility} — ${v.surface} (∠${v.vAngle != null ? v.vAngle.toFixed(2) : '—'}°, on line: [${alignsStr}])`,
                                    penetrationAmount: v.penetration != null ? v.penetration.toFixed(2) : '0.00 (zero-tolerance zone)',
                                });
                            });
                        });
                        exportSurfacePenetratingCsv(entries, radiusKm);
                    }} />
                {shieldOn && hasCnsImpact && (
                    <div style={{ position: 'absolute', top: 226, right: 12, zIndex: 1000, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 8, border: '1px solid #FFB02055', backdropFilter: 'blur(8px)', maxWidth: 260, fontSize: 10, color: T.muted, lineHeight: 1.5 }}>
                        <div style={{ color: '#FFB020', fontWeight: 700, marginBottom: 2 }}>📡 CNS/BRA Surfaces (real check)</div>
                        <div style={{ marginBottom: 4 }}>
                            Project impact on: {Object.keys(cnsSurfaceImpacts).join(', ')}
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 4, color: T.white }}>
                            <input type="checkbox" checked={requireOnLine} onChange={e => setRequireOnLine(e.target.checked)} style={{ accentColor: '#FFB020', cursor: 'pointer' }} />
                            Require candidate on line to navaid to count as true shield
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 4, color: T.white }}>
                            <input type="checkbox" checked={showShieldLines} onChange={e => setShowShieldLines(e.target.checked)} style={{ accentColor: '#32B464', cursor: 'pointer' }} />
                            Show navaid ↔ obstacle lines on map
                        </label>
                        {cnsShieldVerdicts.size > 0
                            ? <span><b style={{ color: '#FFB020' }}>{cnsShieldVerdicts.size}</b> obstacle(s) confirmed to already penetrate as much or more on at least one facility — not a new impact there.</span>
                            : <span>No shielding candidate matches or exceeds the project's own penetration yet.</span>}
                        {cnsShieldRealPenetration.size > 0 && (
                            <div style={{ marginTop: 4 }}>
                                <b style={{ color: '#FFE14D' }}>{cnsShieldRealPenetration.size}</b> candidate(s) genuinely penetrate a real BRA surface at their own position (yellow/combo markers).
                            </div>
                        )}
                    </div>
                )}
                {showHighestElevLines && penetratingObstacleFacilityPairs.length > 0 && (
                    <DraggableModal
                        title="📐 Navaid Masking Angle vs CTR Boundary"
                        onClose={() => setShowHighestElevLines(false)}
                        width={400}
                        height={480}
                    >
                        <div style={{ padding: 12, fontSize: 11, color: T.muted, lineHeight: 1.5, overflowY: 'auto', height: '100%' }}>
                            <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${T.border}33` }}>
                                <label style={{ fontSize: 10, color: T.muted, display: 'block', marginBottom: 4 }}>
                                    Circuit altitude (ft AMSL) — e.g. FAOR 7000
                                </label>
                                <input
                                    type="number"
                                    value={cnsOperationalAlt}
                                    onChange={e => setCnsOperationalAlt(e.target.value)}
                                    placeholder="e.g. 7000"
                                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, background: T.bg0, border: `1px solid ${T.border}`, color: T.white, fontSize: 12 }}
                                />
                            </div>
                            {maskingAngleResults.size > 0 && (
                                <button
                                    onClick={() => {
                                        const headers = [
                                            "Facility", "Obstacle", "Distance Navaid-Obstacle (m)", "Distance Navaid-CTR Boundary (m)",
                                            "Lowest Angle to Detect (deg)", "Altitude at CTR Boundary (m)", "Altitude at CTR Boundary (ft)", "Clearance at Boundary (m)", "Clearance at Boundary (ft)",
                                            "Angle to Reach CTR Circuit (deg)", "Altitude at Obstacle (m)", "Altitude at Obstacle (ft)", "Obstacle Top Elevation (m)", "Clearance at Obstacle (m)", "Clearance at Obstacle (ft)", "Result",
                                        ];
                                        const rows = [...maskingAngleResults.values()].filter(r => !r.error).map(r => [
                                            r.facilityIdent, r.obstacle.name, r.data.distance_to_obstacle_m, r.data.distance_to_boundary_m,
                                            r.data.masking_angle_deg, r.data.masked_altitude_at_boundary_m, r.data.masked_altitude_at_boundary_ft, r.data.clearance_m, r.data.clearance_ft,
                                            r.data.required_angle_deg, r.data.critical_altitude_at_obstacle_m, r.data.critical_altitude_at_obstacle_ft, parseFloat(r.obstacle.elevation_m || 0),
                                            r.data.clearance_at_obstacle_m, r.data.clearance_at_obstacle_ft,
                                            r.data.clearance_at_obstacle_ft < 0 ? 'NOT CLEAR' : 'CLEAR',
                                        ]);
                                        exportToCsv(headers, rows, `${(project?.name || 'Project').replace(/[\/\\]/g, '_')}_Navaid_Masking_Angle.csv`);
                                    }}
                                    style={{ padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', background: '#7B61FF', color: T.white, fontSize: 10, fontWeight: 700, marginBottom: 10 }}
                                >
                                    📥 Export CSV
                                </button>
                            )}
                            {maskingAngleLoading && <div>Computing…</div>}
                            {!maskingAngleLoading && maskingAngleResults.size === 0 && <div>No result — check the project has an aerodrome CTR loaded.</div>}
                            {[...maskingAngleResults.values()].map(r => (
                                <div key={r.key} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: `1px solid ${T.border}33` }}>
                                    <div style={{ color: T.white, fontWeight: 600, marginBottom: 4 }}>{r.facilityIdent} → {r.obstacle.name}</div>
                                    {r.error ? (
                                        <div style={{ color: '#e84040' }}>{r.error}</div>
                                    ) : (
                                        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: '2px 0', color: T.muted, fontSize: 10 }}>Distance navaid → obstacle</td>
                                                    <td style={{ padding: '2px 0', color: T.muted, textAlign: 'right', fontSize: 10 }}>{r.data.distance_to_obstacle_m.toFixed(0)} m</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: '2px 0', color: T.muted, fontSize: 10 }}>Distance navaid → CTR boundary</td>
                                                    <td style={{ padding: '2px 0', color: T.muted, textAlign: 'right', fontSize: 10 }}>{r.data.distance_to_boundary_m.toFixed(0)} m</td>
                                                </tr>
                                                <tr><td colSpan={2} style={{ height: 6 }} /></tr>
                                                <tr>
                                                    <td style={{ padding: '2px 0', color: r.data.shadowed_at_floor ? '#e84040' : '#32B464' }}>Lowest angle to detect</td>
                                                    <td style={{ padding: '2px 0', color: r.data.shadowed_at_floor ? '#e84040' : '#32B464', textAlign: 'right', fontWeight: 700 }}>{r.data.masking_angle_deg}°</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: '2px 0', color: T.muted, fontSize: 10 }}>— CTR boundary altitude (on that angle)</td>
                                                    <td style={{ padding: '2px 0', color: T.muted, textAlign: 'right', fontSize: 10 }}>{r.data.masked_altitude_at_boundary_m.toFixed(1)} m / {r.data.masked_altitude_at_boundary_ft.toFixed(0)} ft</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: '2px 0', color: r.data.clearance_at_obstacle_ft < 0 ? '#e84040' : '#32B464', fontWeight: 700, fontSize: 10, letterSpacing: 0.3 }}>ANGLE TO REACH CTR CIRCUIT</td>
                                                    <td style={{ padding: '2px 0', color: r.data.clearance_at_obstacle_ft < 0 ? '#e84040' : '#32B464', textAlign: 'right', fontWeight: 800 }}>{r.data.required_angle_deg}°</td>
                                                </tr>
                                                <tr><td colSpan={2} style={{ height: 6 }} /></tr>
                                                <tr>
                                                    <td style={{ padding: '2px 0', color: T.muted }}>Altitude at obstacle (on that angle)</td>
                                                    <td style={{ padding: '2px 0', color: T.white, textAlign: 'right', fontWeight: 700 }}>{r.data.critical_altitude_at_obstacle_m.toFixed(1)} m / {r.data.critical_altitude_at_obstacle_ft.toFixed(0)} ft</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: '2px 0', color: T.muted, fontSize: 10 }}>— vs obstacle top elevation</td>
                                                    <td style={{ padding: '2px 0', color: T.muted, textAlign: 'right', fontSize: 10 }}>{(parseFloat(r.obstacle.elevation_m || 0)).toFixed(1)} m / {(parseFloat(r.obstacle.elevation_m || 0) * 3.28084).toFixed(0)} ft</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: '2px 0', color: T.muted, fontWeight: 700 }}>Clearance at obstacle</td>
                                                    <td style={{ padding: '2px 0', color: T.white, textAlign: 'right', fontWeight: 800 }}>{r.data.clearance_at_obstacle_m.toFixed(1)} m / {r.data.clearance_at_obstacle_ft.toFixed(0)} ft</td>
                                                </tr>
                                                <tr>
                                                    <td colSpan={2} style={{ padding: '4px 0 0', textAlign: 'center', fontWeight: 800, color: r.data.clearance_at_obstacle_ft < 0 ? '#e84040' : '#32B464' }}>
                                                        {r.data.clearance_at_obstacle_ft < 0 ? 'NOT CLEAR' : 'CLEAR'}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            ))}
                        </div>
                    </DraggableModal>
                )}
                <MapMeasureTool map={mapState} top={52} tableOffset={resultsCollapsed ? 40 : 220} />
                <MapCoordinateViewer map={mapState} />
                <div ref={mapRef} style={{ flex: 1 }} />

                {/* Results table */}
                <div style={{ height: resultsCollapsed ? 38 : resultsHeight, background: T.bg1, borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', transition: 'height 0.2s ease', overflow: 'hidden', flexShrink: 0 }}>
                        {!resultsCollapsed && <ResizeHandle onMouseDown={startResultsResize} />}
                    <div style={{ padding: '8px 20px', background: T.bg0, borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.white, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🏔</span> CNS Results
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            {!resultsPoppedOut && (
                                <button
                                    onClick={() => {
                                        setResultsPoppedOut(true);
                                        setResultsCollapsed(true);
                                    }}
                                    style={{
                                        background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                        display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600
                                    }}
                                    title="Pop out results to a movable window"
                                >
                                    🗖 Pop Out
                                </button>
                            )}
                            <button
                                onClick={() => setResultsCollapsed(!resultsCollapsed)}
                                style={{
                                    background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                    fontWeight: 600
                                }}
                            >
                                {resultsCollapsed ? '▲ Expand' : '▼ Collapse'}
                            </button>
                        </div>
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        {resultsPoppedOut ? (
                            <div style={{ padding: 20, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                                Results are popped out. 
                                <button 
                                    onClick={() => { setResultsPoppedOut(false); setResultsCollapsed(false); }} 
                                    style={{ background: 'none', border: 'none', color: T.blue, textDecoration: 'underline', cursor: 'pointer', marginLeft: 6, fontWeight: 600 }}
                                >
                                    Bring back
                                </button>
                            </div>
                        ) : renderResultsContent()}
                    </div>
                </div>
            </div>

            {resultsPoppedOut && (
                <DraggableModal
                    title={`${project?.name || ''} - CNS Results`}
                    onClose={() => { setResultsPoppedOut(false); setResultsCollapsed(false); }}
                    width={850}
                    height={500}
                >
                    {renderResultsContent()}
                </DraggableModal>
            )}

            {show3DPreview && (
                <Cesium3DPreviewModal
                    title={`${project?.name || 'Project'} — CNS BRA 3D Preview`}
                    fetchKmz={() => buildCnsBraKmzBlob('3d')}
                    onClose={() => setShow3DPreview(false)}
                />
            )}
        </div>
    );
}
